import { useState, useEffect, useRef } from 'react';

interface UseOptimizedImageOptions {
  src: string;
  priority?: boolean;
  rootMargin?: string;
}

interface UseOptimizedImageReturn {
  ref: React.RefObject<HTMLImageElement | null>;
  src: string | undefined;
  loading: 'eager' | 'lazy';
  decoding: 'async' | 'auto' | 'sync';
  fetchPriority: 'high' | 'low' | 'auto';
  onLoad: () => void;
  style: React.CSSProperties;
}

/**
 * Progressive image loading hook with Intersection Observer
 * 
 * Features:
 * - Loads images 200px before viewport (configurable)
 * - Falls back gracefully if IntersectionObserver unavailable
 * - Fade-in animation on load
 * - GPU-accelerated transitions
 * - Respects fetchpriority for critical images
 * 
 * @param options - Configuration options
 * @returns Image props and handlers
 */
export function useOptimizedImage({
  src,
  priority = false,
  rootMargin = '200px'
}: UseOptimizedImageOptions): UseOptimizedImageReturn {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    // Progressive enhancement: only use IntersectionObserver if available
    if (!priority && typeof IntersectionObserver !== 'undefined' && imgRef.current) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        },
        { rootMargin }
      );
      
      observer.observe(imgRef.current);
      
      return () => {
        observer.disconnect();
      };
    } else if (!priority) {
      // Fallback for browsers without IntersectionObserver
      setIsInView(true);
    }
    return undefined;
  }, [priority, rootMargin]);

  return {
    ref: imgRef,
    src: isInView ? src : undefined,
    loading: priority ? 'eager' : 'lazy',
    decoding: 'async',
    fetchPriority: priority ? 'high' : 'low',
    onLoad: () => setIsLoaded(true),
    style: {
      opacity: isLoaded ? 1 : 0,
      transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      transform: 'translateZ(0)' // GPU acceleration
    }
  };
}

